import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { Sidebar } from '@/components/sidebar';
import { useSession } from '@/lib/queries/session';

/**
 * App layout route — wraps every authenticated page.
 *
 * Guard strategy:
 *   1. Cache-first: react-query returns stale session immediately if present.
 *   2. If session is `null` and not loading → redirect to /login.
 *   3. If still loading → show spinner.
 */
export const Route = createFileRoute('/(app)')({
  beforeLoad: ({ location }) => {
    // We don't pre-fetch session here — we let the React Query cache decide.
    // To block navigation before session check, add a loader.
    return { redirectPath: location.href };
  },
  component: AppLayout,
});

function AppLayout() {
  const { data: session, isLoading } = useSession();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!session) {
    throw redirect({
      to: '/login',
      search: { redirect: window.location.pathname },
      replace: true,
    });
  }

  return (
    <div className="flex h-screen">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
